import { useCallback, useMemo } from "react";
import { Answerer } from "../../../api/generated";
import { parseToDate } from "../../../libs/helper";
import {
    getAnswerer,
    sendVerifyEmailToAnswerer,
    updateAnswerer,
    uploadProfileImage,
} from "../../../api/answerer";
import { useAuth } from "../../auth/hooks/useAuth";
import { toFormatString } from "../../../libs/helper";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { showToast } from "@twioku/common_components";
import { type UpdateAnswererInput } from "../schemas/answererSchemas";

export const useAnswerer = (id: string) => {
    const queryKey = useMemo(() => ["answerer", id], [id]);
    const queryClient = useQueryClient();

    const { data: answerer, isLoading } = useQuery(queryKey, () =>
        getAnswerer(id)
    );

    const clearCache = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey });
    }, [queryClient, queryKey]);

    return {
        answerer,
        isLoading,
        clearCache,
    };
};

export const useUpdateAnswerer = (answerer: Answerer) => {
    const { csrfToken } = useAuth();

    const mutation = useMutation({
        mutationFn: async (input: UpdateAnswererInput) => {
            await updateAnswerer(
                answerer.answererId,
                input.name,
                input.receiverNickname,
                input.email,
                input.birthday
                    ? toFormatString(parseToDate(input.birthday), true, "-")
                    : undefined,
                input.relationship,
                input.questionSendType,
                input.qmailFrequency,
                csrfToken
            );
        },
    });

    return { updateAnswerer: mutation };
};

export const useUploadAnswererProfileImage = (answerer: Answerer) => {
    const { csrfToken } = useAuth();

    const mutation = useMutation({
        mutationFn: async (file: File) => {
            const validatedResult = validateAnswererProfileImage(file);
            if (!validatedResult.isSuccess) {
                showToast(validatedResult.message, "error");
                return;
            }

            return await uploadProfileImage(
                answerer.answererId,
                file,
                csrfToken
            );
        },
    });

    const validateAnswererProfileImage = (
        file: File
    ): { isSuccess: boolean; message: string } => {
        const allowExtensions = ".(jpeg|jpg|png|gif)$";
        if (!file.name.match(allowExtensions)) {
            return {
                isSuccess: false,
                message: "jpeg, jpg, png, gifファイルを選択してください。",
            };
        }

        const sizeLimit = 1024 * 1024 * 5; // 5MB
        if (file.size > sizeLimit) {
            return {
                isSuccess: false,
                message: "ファイルサイズは5MB以下にしてください。",
            };
        }

        return {
            isSuccess: true,
            message: "",
        };
    };

    return {
        uploadAnswererProfileImage: mutation,
        validateAnswererProfileImage,
    };
};

export const useSendVerifyEmailToAnswerer = (answerer: Answerer) => {
    const { csrfToken } = useAuth();

    const mutation = useMutation({
        mutationFn: async () => {
            await sendVerifyEmailToAnswerer(answerer.answererId, csrfToken);
        },
    });

    return { sendVerifyEmailToAnswerer: mutation };
};
